import React, { Fragment } from 'react';
import { always, has, head, ifElse, isNil, last, pathOr, pipe, prop, propEq, props, reduce, sort } from 'ramda';
import { Group } from '@vx/group';
import { Circle, LinePath } from '@vx/shape';
import { scalePoint, scaleLinear } from '@vx/scale';
import { ParentSize } from '@vx/responsive';
import { useIntl } from 'gatsby-plugin-intl';
import { filterOffNils } from '../../../utils';
import { toFixed } from '../../../utils/math';
import { TRANSPARENT } from '../../../utils/colors';
import { renderWhenTrueOtherwise } from '../../../utils/rendering';
import { useBreakpointsContext } from '../../../store/BreakpointsContext';
import { useChartTooltipContext } from '../../../store/ChartTooltipContext';
import { TooltipElementTable, TooltipTableDataCell } from '../../tooltipElementTable/tooltipElementTable.styles';
import { NoData, isNoData } from '../noData';
import { Header } from '../header';
import { Axes } from '../axes';
import { Grid } from '../grid';
import { SLOPE_DEFAULT } from '../configs/slope.config';
import { Container, Chart, Svg } from '../charts.styles';
import { CHART_TYPES, DEFAULT_SIZE } from '../charts.constants';
import { renderOpenTooltip } from '../tooltipBasic/tooltipBasic.utils';
import { TooltipWithPortal } from '../tooltipWithPortal';
import { AUTO, START, END } from './slopeChart.constants';
import { AxisLabel, DotLabel, LineGroup, MoreText, ItemTooltipList } from './slopeChart.styles';

const getValues = (data, series) =>
  pipe(
    reduce((list, item) => {
      list.push(...props(series, item));

      return list;
    }, []),
    filterOffNils,
    sort((a, b) => a - b)
  )(data);

const getDotColor = ifElse(propEq('dotFill', AUTO), (_, color) => color, prop('dotFill'));

const renderDots = (data, color, config) =>
  data.map(({ x, y }, index) => (
    <Circle
      key={index}
      cx={x}
      cy={y}
      r={config.dotRadius}
      fill={getDotColor(config, color)}
      shapeRendering="geometricPrecision"
    />
  ));

const renderLabels = (data, color, config) =>
  config.showValues &&
  data.map(({ x, y, value }, index) => (
    <DotLabel key={index} xDot={x} yDot={y} color={color} fontSize={config.fontSize}>
      {value}
    </DotLabel>
  ));

const renderAxisLabels = ({ data, yScale, xMargin, axis, series, config, isMobile, tooltip }) => {
  const serie = head(series);
  const values = [];
  const filteredData = data.reduce((list, item) => {
    const value = item[serie] && Math.round(item[serie] * 10) / 10;

    if (isNil(value)) return list;
    if (has(value, list)) {
      list[value].push(item[axis]);
    } else {
      list[value] = [item[axis]];
      values.push(value);
    }

    return list;
  }, {});

  values.sort();

  const yPositions = values.map((value) => yScale(value));
  const x = xMargin - config.labelAxisMargin;
  const addComma = (index, list) => index < list.length - 1 && ',';

  const handleHover = (list) => (e) => {
    const barRect = e.target.getBoundingClientRect();
    const top = barRect.top;
    const left = barRect.left;

    if (left === tooltip.left && top === tooltip.top) {
      return;
    }

    tooltip.show({
      data: {
        list,
      },
      top: top,
      left: left,
    });
  };

  const getContent = renderWhenTrueOtherwise(
    (_, list) => {
      const item = `${list[0]}`;
      const text = item.length > 10 && list.length > 1 ? `${item.slice(0, 10)}...` : item;
      const counter = list.length - 1;

      return (
        item && (
          <Fragment>
            <tspan>{text}</tspan>
            {counter > 0 && <MoreText> +{counter} more</MoreText>}
          </Fragment>
        )
      );
    },
    (_, list) => (
      <Fragment>
        {list.map((text, index) => (
          <tspan key={index}>
            {text}
            {addComma(index, list)}{' '}
          </tspan>
        ))}
      </Fragment>
    )
  );

  const hoverProps = (list) => (isMobile ? tooltip.bindHoverEvents(handleHover(list)) : {});

  return (
    <Fragment>
      {values.map((value, valueIndex) => (
        <AxisLabel
          key={valueIndex}
          xDot={x}
          yDot={yPositions[valueIndex]}
          textAnchor="end"
          fontSize={config.fontSize}
          {...hoverProps(filteredData[value])}
        >
          {getContent(isMobile, filteredData[value])}
        </AxisLabel>
      ))}
    </Fragment>
  );
};

const renderLines = ({ xScale, yScale, axis, series, config, tooltip, isHoverActive, hoveredGroupIndex }) => (
  item,
  index
) => {
  const axisLabels = [];
  const data = series.reduce((list, serie, serieIndex) => {
    const rawValue = item[serie];
    if (!isNil(rawValue)) {
      const value = Math.round(item[serie] * 10) / 10;
      const x = xScale(serie);
      const y = yScale(value);

      list.push({ x, y, value });

      if (!serieIndex) {
        axisLabels.push({
          textAnchor: END,
          text: item[axis],
          x: x - config.yAxisOffset,
          y,
        });
      }
    }

    return list;
  }, []);

  const color = config.defaultColor;

  const handleHover = (e) => {
    const barRect = e.target.getBoundingClientRect();
    const top = barRect.top;
    const left = barRect.left;

    if (left === tooltip.left && top === tooltip.top && tooltip.data.index === index) {
      return;
    }

    tooltip.show({
      data: {
        data: { ...data },
        index,
        label: axisLabels[0].text,
        color,
      },
      top: top,
      left: left,
    });
  };

  const isGroupHovered = hoveredGroupIndex === index;

  return (
    <LineGroup key={index} isHoverActive={isHoverActive} isGroupHovered={isGroupHovered}>
      <LinePath
        data={data}
        x={prop('x')}
        y={prop('y')}
        stroke={color}
        strokeWidth={isHoverActive && isGroupHovered ? config.hoverStrokeWidth : config.strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
      <Group>
        {renderDots(data, color, config)}
        {renderLabels(data, color, config)}
      </Group>
      <LinePath
        data={data}
        x={prop('x')}
        y={prop('y')}
        stroke={TRANSPARENT}
        strokeWidth={config.hoverAreaStrokeWidth}
        fill="none"
        {...tooltip.bindHoverEvents(handleHover)}
      />
    </LineGroup>
  );
};

export const SlopeChart = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  data: chartData = {},
  tabs,
  parser: customParser,
  config: customConfig = {},
}) => {
  if (customParser) customParser(chartData);
  const tooltip = useChartTooltipContext();
  const intl = useIntl();
  chartData['title'] = intl.formatMessage({ id: chartData.title });
  const { title, subtitle, data, axis, labels, series } = chartData;

  if (isNoData(data, axis, labels, series)) return <NoData width={width} height={height} />;

  const config = {
    ...SLOPE_DEFAULT,
    ...customConfig,
  };

  const xScale = scalePoint({
    domain: series,
  });

  const values = getValues(data, series);
  const min = head(values);
  const max = last(values);

  const yScale = scaleLinear({
    domain: [min, max],
  });

  const yUsed = {
    [START]: {},
    [END]: {},
  };

  const renderTooltipContent = () => {
    if (isNil(tooltip.data)) {
      return null;
    }

    if (tooltip.data.list) {
      return <ItemTooltipList>{tooltip.data.list.join(', ')}</ItemTooltipList>;
    }

    const data = Object.values(tooltip.data.data) || [{ value: 0 }];
    const change = -data[0].value + data[data.length - 1].value;

    const categoryKey = axis.charAt(0).toUpperCase() + axis.slice(1);
    const VALUE_COLUMN_NAME = 'change';
    const { formatMessage } = useIntl();

    return (
      <TooltipElementTable>
        <thead>
          <tr>
            <th>{formatMessage({ id: categoryKey })}</th>
            <th>{formatMessage({ id: VALUE_COLUMN_NAME })}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <TooltipTableDataCell>{formatMessage({ id: tooltip.data.label })}</TooltipTableDataCell>
            <TooltipTableDataCell color={tooltip.data.color}>
              <span>{toFixed(change, 2)}</span>
            </TooltipTableDataCell>
          </tr>
        </tbody>
      </TooltipElementTable>
    );
  };

  const { isMobile } = useBreakpointsContext();
  const hoveredGroupIndex = pathOr(null, ['data', 'index'], tooltip);
  const isHoverActive = !isNil(hoveredGroupIndex);

  return (
    <Container width={width} height={height}>
      <Header title={title} subtitle={subtitle} tabs={tabs} config={config} />
      <Chart>
        {renderOpenTooltip(always(<TooltipWithPortal>{renderTooltipContent()}</TooltipWithPortal>), tooltip)}

        <ParentSize>
          {({ width: chartAreaWidth, height: chartAreaHeight }) => {
            if (!chartAreaWidth || !chartAreaHeight) return null;

            const gridWidth = chartAreaWidth - config.xAxisSpace;
            const xMargin = gridWidth * config.labelSpace;
            const chartHeight = chartAreaHeight - config.yAxisSpace;
            const chartWidth = gridWidth - xMargin;

            xScale.range([xMargin, gridWidth]);
            yScale.range([chartHeight - config.verticalMargin, config.verticalMargin]);

            return (
              <Svg>
                <Grid width={gridWidth} height={chartHeight} config={config} yScale={yScale}>
                  {config.xAxisBackgroundVisible && (
                    <rect x={xMargin} width={chartWidth} height={chartHeight} fill={config.xAxisBackground} />
                  )}
                  <Group>
                    {data.map(
                      renderLines({
                        xScale,
                        yScale,
                        axis,
                        series,
                        config,
                        yUsed,
                        tooltip,
                        isHoverActive,
                        hoveredGroupIndex,
                      })
                    )}
                  </Group>
                  {renderAxisLabels({ data, yScale, xMargin, axis, series, config, isMobile, tooltip })}
                </Grid>
                <Axes
                  left={-config.xAxisSpace}
                  type={CHART_TYPES.VERTICAL}
                  chartHeight={chartHeight}
                  chartWidth={chartAreaWidth}
                  config={config}
                  groupScale={xScale}
                  valueScale={yScale}
                />
              </Svg>
            );
          }}
        </ParentSize>
      </Chart>
    </Container>
  );
};
